import { HTTP_STATUS_CODES } from '@showbie-socrative/socrative-utils';

import { ImportFromTextEventAdapter } from '~analytics/adapters/ImportFromTextEventAdapter';

import localAxios from '../localAxios';
import { Order, Question } from '../responses';
import { importFromDocOrTextURL } from './urls';

interface ImportFromTextParams {
  quizId: number;
  workspaceId?: string;
  workspaceQuizId?: string;
  insertionOrder: number;
  textQuestions?: string;
  fileKey?: string;
  includeExplanation?: boolean;
}

interface QuestionResponse {
  questions: Question[];
  orders: Order[];
}

export interface ImportFromTextResponse {
  result: QuestionResponse;
  elapsedTime: number;
  promptVersion: string;
  version: string;
  usage: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

export interface AsyncImportFromTextResponse {
  async: true;
  requestId: string;
  fileKey: string;
  message: string;
  elapsedTime: number;
}

export async function importFromText({
  quizId,
  workspaceId,
  workspaceQuizId,
  insertionOrder,
  textQuestions,
  fileKey,
  includeExplanation,
}: ImportFromTextParams) {
  const importURL = importFromDocOrTextURL(quizId);

  const { data, status } = await localAxios.post<
    ImportFromTextResponse | AsyncImportFromTextResponse
  >(importURL.href, {
    workspaceId,
    workspaceQuizId,
    textQuestions,
    insertionOrder,
    fileKey,
    includeExplanation,
  });

  // Check if async response (file-based extraction)
  if (
    'async' in data &&
    data.async &&
    status === HTTP_STATUS_CODES.HTTP_202_ACCEPTED
  ) {
    return {
      async: true,
      requestId: data.requestId,
      fileKey: data.fileKey,
      elapsedTime: data.elapsedTime,
    };
  }

  const { result, version, promptVersion, elapsedTime, usage } =
    data as ImportFromTextResponse;

  // Analytics
  new ImportFromTextEventAdapter({
    numQuestions: result.questions.length,
    promptLength: textQuestions.length,
    promptVersion,
    version: version,
    responseMilliseconds: elapsedTime,
    promptTokens: usage.promptTokens,
    completionTokens: usage.completionTokens,
    totalTokens: usage.totalTokens,
  }).sendAnalytics();

  return {
    questions: result.questions,
    orders: result.orders,
    elapsedTime: elapsedTime,
    version: version,
  };
}
